import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { BrandKit } from 'types'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

interface Props {
  id: number
}

interface Data {
  brand_kit: BrandKit
}

interface Context {
  previousBrandKit?: BrandKit
}

export const retryBrandKit = async ({ id, workspaceId }: Props & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${id}/retry`, {
    method: 'POST',
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to retry brand kit', {
      metaData: {
        id,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useRetryBrandKit = ({ id }: Props) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, void, Context>(
    ['workspaces', currentWorkspaceId, 'brandKits', 'detail', id],
    () => {
      return retryBrandKit({ id, workspaceId: currentWorkspaceId })
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', id])
        const previousBrandKit = queryClient.getQueryData([
          'workspaces',
          currentWorkspaceId,
          'brandKit',
          'detail',
          id,
        ]) as BrandKit | undefined
        queryClient.setQueryData(['workspaces', currentWorkspaceId, 'brandKits', 'detail', id], (old) => ({
          ...(old as Data),
        }))
        return { previousBrandKit }
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(
          ['workspaces', currentWorkspaceId, 'brandKits', 'detail', id],
          context?.previousBrandKit,
        )
      },
      onSettled: () => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'list'])
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', id])
      },
    },
  )
}
